package com.hyperether.goodjob.scenes.jobs

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Paint
import androidx.compose.ui.graphics.PaintingStyle
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.add_more
import com.hyperether.goodjob.resources.additional_notes
import com.hyperether.goodjob.resources.baseline_file_present_24
import com.hyperether.goodjob.resources.client_feedback
import com.hyperether.goodjob.resources.client_signature
import com.hyperether.goodjob.resources.comment
import com.hyperether.goodjob.resources.damage_desc
import com.hyperether.goodjob.resources.damage_title
import com.hyperether.goodjob.resources.enter_feedback
import com.hyperether.goodjob.resources.feedback
import com.hyperether.goodjob.resources.final_inspection
import com.hyperether.goodjob.resources.final_inspection_desc
import com.hyperether.goodjob.resources.final_inspection_fail
import com.hyperether.goodjob.resources.max_size
import com.hyperether.goodjob.resources.no
import com.hyperether.goodjob.resources.no_damage
import com.hyperether.goodjob.resources.only_support
import com.hyperether.goodjob.resources.remove_file
import com.hyperether.goodjob.resources.safety_requirements
import com.hyperether.goodjob.resources.safety_requirements_desc
import com.hyperether.goodjob.resources.safety_requirements_title
import com.hyperether.goodjob.resources.submit
import com.hyperether.goodjob.resources.trash
import com.hyperether.goodjob.resources.try_another_document
import com.hyperether.goodjob.resources.upload_cloud
import com.hyperether.goodjob.resources.upload_documents
import com.hyperether.goodjob.resources.upload_photos_of_completed_job
import com.hyperether.goodjob.resources.upload_signature
import com.hyperether.goodjob.resources.was_damage_caused
import com.hyperether.goodjob.resources.yes
import com.hyperether.goodjob.resources.your_comment
import com.hyperether.goodjob.scenes.components.clickableWithPointer
import com.hyperether.goodjob.scenes.register.AppInputField
import com.hyperether.goodjob.scenes.register.FilledButton
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.DarkGrey
import com.hyperether.goodjob.theme.RedInactive
import com.hyperether.goodjob.util.DocumentType
import com.hyperether.goodjob.util.UploadManager
import com.hyperether.goodjob.util.UploadStatus
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun CompletionReportSubmitView(
    job: Job,
    report: CompletionReportResponse?,
    viewModel: JobDetailsViewModel,
    submit: () -> Unit,
    onUploadDocumentClick: (String, DocumentType) -> Unit
) {
    val fileIds by viewModel.fileIds.collectAsState(initial = emptyList())
    val clientSignaturePainters by viewModel.clientSignaturePainters.collectAsState(initial = emptyList())
    val clientSignaturePdfs by viewModel.clientSignaturePdfs.collectAsState(initial = emptyList())
    val signatureFileId by viewModel.clientSignatureFileId.collectAsState(initial = null)
    LaunchedEffect(Unit) {
        viewModel.downloadCompletionReportFiles(report)
    }
    LaunchedEffect(clientSignaturePainters) {
        clientSignaturePainters.firstOrNull()?.let {
            viewModel.clientSignatureFileId.value = it.first
        } ?: run {
            viewModel.clientSignatureFileId.value = null
        }
    }
    LaunchedEffect(clientSignaturePdfs) {
        clientSignaturePdfs.firstOrNull()?.let {
            viewModel.clientSignatureFileId.value = it.first
        } ?: run {
            viewModel.clientSignatureFileId.value = null
        }
    }

    val pathEffectLine =
        PathEffect.dashPathEffect(floatArrayOf(10f, 10f), phase = 0f)
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.toScaleDp())
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter),
        ) {
            // Title
            Text(
                text = stringResource(Res.string.upload_photos_of_completed_job),
                fontWeight = FontWeight.W700,
                fontSize = 16.toScaleSp(),
            )
            Spacer(modifier = Modifier.height(12.toScaleDp()))
            Box(
                modifier = Modifier.fillMaxWidth().drawBehind {
                    val paint = Paint().apply {
                        color = BlueApp
                        style = PaintingStyle.Stroke
                        strokeWidth = Dp(2f).toPx()
                        pathEffect = pathEffectLine
                    }

                    drawIntoCanvas { canvas ->
                        canvas.drawRoundRect(
                            0f,
                            0f,
                            size.width,
                            size.height,
                            radiusX = 20f,
                            radiusY = 20f,
                            paint
                        )
                    }
                }.padding(24.toScaleDp()),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.clickableWithPointer {
                        onUploadDocumentClick(job.id!!, DocumentType.REPORT_FILE)
                    })
                {
                    if (fileIds.isEmpty()) {
                        Image(
                            painter = painterResource(Res.drawable.upload_cloud),
                            contentDescription = "",
                            modifier = Modifier.size(40.toScaleDp())
                        )

                        Spacer(modifier = Modifier.height(8.toScaleDp()))

                        DisableSelection {
                            Text(
                                text = stringResource(Res.string.upload_documents),
                                color = Color.Black,
                                fontSize = 16.toScaleSp(),
                                fontWeight = FontWeight.W700
                            )
                        }

                        Spacer(modifier = Modifier.height(4.toScaleDp()))

                        Text(
                            text = stringResource(Res.string.max_size),
                            color = Color.LightGray,
                            fontSize = 16.toScaleSp()
                        )
                    } else if (viewModel.isFileUploadError()) {
                        Text(
                            text = stringResource(Res.string.try_another_document),
                            color = Color.Gray,
                            fontWeight = FontWeight.Normal,
                            fontSize = 16.toScaleSp()
                        )
                    } else {
                        Image(
                            painter = painterResource(Res.drawable.baseline_file_present_24),
                            contentDescription = "",
                            modifier = Modifier.padding(top = 24.toScaleDp()).size(40.toScaleDp())
                        )
                        Column(
                            modifier = Modifier
                                .padding(horizontal = 10.toScaleDp())
                        ) {
                            if (fileIds.isNotEmpty())
                                for (fileId in fileIds) {
                                    Row(
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically,
                                        modifier = Modifier.fillMaxWidth()
                                    ) {
                                        Text(
                                            text = fileId,
                                            color = DarkGrey,
                                            fontWeight = FontWeight.Normal,
                                            fontSize = 16.toScaleSp(),
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis,
                                            modifier = Modifier
                                                .weight(1f)
                                                .padding(end = 8.toScaleDp())
                                        )
                                        DisableSelection {
                                            Image(
                                                painter = painterResource(Res.drawable.trash),
                                                contentDescription = null,
                                                modifier = Modifier
                                                    .size(20.toScaleDp())
                                                    .clickableWithPointer {
                                                        viewModel.deleteFile(fileId)
                                                    }
                                            )
                                        }
                                    }
                                    Spacer(modifier = Modifier.height(10.toScaleDp()))
                                }
                        }
                        Spacer(modifier = Modifier.height(12.toScaleDp()))
                        DisableSelection {
                            Text(
                                text = stringResource(Res.string.add_more),
                                color = BlueApp,
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.toScaleSp(),
                                modifier = Modifier
                                    .padding(bottom = 24.toScaleDp())
                                    .clickableWithPointer {
                                        onUploadDocumentClick(job.id!!, DocumentType.REPORT_FILE)
                                    }
                            )
                        }
                    }
                }
            }
            Text(
                text = stringResource(Res.string.only_support),
                fontSize = 12.toScaleSp(),
                color = Color.LightGray,
                textAlign = TextAlign.Center,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            CheckBoxMultipleItemBoldTitle(
                question = stringResource(Res.string.was_damage_caused),
                answerList = arrayListOf(
                    stringResource(Res.string.no_damage),
                    stringResource(Res.string.yes)
                ),
                checkedList = arrayListOf(
                    !viewModel.damageState.value,
                    viewModel.damageState.value
                ),
                onSelectedAnswer = { answerIndex ->
                    when (answerIndex) {
                        0 -> {
                            viewModel.damageState.value = false
                        }

                        1 -> {
                            viewModel.damageState.value = true
                        }
                    }
                }
            )
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            if (viewModel.damageState.value) {
                AppInputField(
                    viewModel.damageTextState,
                    stringResource(Res.string.damage_title),
                    stringResource(Res.string.damage_desc),
                    isMaxLinesDefined = false,
                    paddingTop = 0.toScaleDp()
                )
            }
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            CheckBoxMultipleItemBoldTitle(
                question = stringResource(Res.string.final_inspection),
                answerList = arrayListOf(
                    stringResource(Res.string.yes),
                    stringResource(Res.string.no)
                ),
                checkedList = arrayListOf(
                    viewModel.correctFunctionalityState.value,
                    !viewModel.correctFunctionalityState.value
                ),
                onSelectedAnswer = { answerIndex ->
                    when (answerIndex) {
                        0 -> {
                            viewModel.correctFunctionalityState.value = true
                        }

                        1 -> {
                            viewModel.correctFunctionalityState.value = false
                        }
                    }
                }
            )
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            if (!viewModel.correctFunctionalityState.value) {
                AppInputField(
                    viewModel.correctFunctionalityTextState,
                    stringResource(Res.string.final_inspection_fail),
                    stringResource(Res.string.final_inspection_desc),
                    isMaxLinesDefined = false,
                    paddingTop = 0.toScaleDp()
                )
            }
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            Text(
                stringResource(Res.string.client_feedback),
                fontWeight = FontWeight.W700,
                fontSize = 16.toScaleSp()
            )
            Spacer(modifier = Modifier.height(8.toScaleDp()))
            AppInputField(
                viewModel.clientFeedbackText,
                stringResource(Res.string.feedback),
                stringResource(Res.string.enter_feedback),
                isMaxLinesDefined = false,
                paddingTop = 0.toScaleDp()
            )
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            Row(modifier = Modifier.fillMaxWidth()) {
                Text(
                    stringResource(Res.string.client_signature),
                    style = MaterialTheme.typography.h6,
                    fontSize = 16.toScaleSp(),
                    fontWeight = FontWeight.W700,
                    textAlign = TextAlign.Start
                )
                if (signatureFileId != null) {
                    Spacer(modifier = Modifier.weight(1f))
                    DisableSelection {
                        Text(
                            stringResource(Res.string.remove_file),
                            style = MaterialTheme.typography.h6,
                            fontSize = 16.toScaleSp(),
                            fontWeight = FontWeight.W700,
                            color = RedInactive,
                            textAlign = TextAlign.End,
                            modifier = Modifier.padding(end = 8f.toScaleDp())
                                .clickableWithPointer {
                                    signatureFileId?.let {
                                        UploadManager.removeFile(it)
                                        viewModel.clientSignatureFileId.value = null
                                    }
                                },
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(8.toScaleDp()))
            Box(
                modifier = Modifier.fillMaxWidth().drawBehind {
                    val paint = Paint().apply {
                        color = BlueApp
                        style = PaintingStyle.Stroke
                        strokeWidth = Dp(2f).toPx()
                        pathEffect = pathEffectLine
                    }
                    drawIntoCanvas { canvas ->
                        canvas.drawRoundRect(
                            0f,
                            0f,
                            size.width,
                            size.height,
                            radiusX = 20f,
                            radiusY = 20f,
                            paint
                        )
                    }
                }.padding(24.toScaleDp()),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.clickableWithPointer {
                        if (signatureFileId == null) { // allow only one signature at the time
                            onUploadDocumentClick(job.id!!, DocumentType.REPORT_SIGNATURE)
                        }
                    }) {
                    val uploadStatusSignature = viewModel.getSignatureUploadStatus()
                    if (uploadStatusSignature == UploadStatus.NONE) {
                        Image(
                            painter = painterResource(Res.drawable.upload_cloud),
                            contentDescription = "",
                            modifier = Modifier.size(40.toScaleDp())
                        )

                        Spacer(modifier = Modifier.height(8.toScaleDp()))

                        DisableSelection {
                            Text(
                                text = stringResource(Res.string.upload_signature),
                                color = Color.Black,
                                fontSize = 16.toScaleSp(),
                                textAlign = TextAlign.Center
                            )
                        }
                    } else if (uploadStatusSignature == UploadStatus.ERROR) {
                        Text(
                            text = stringResource(Res.string.try_another_document),
                            color = Color.Gray,
                            fontSize = 16.toScaleSp(),
                            fontWeight = FontWeight.Normal
                        )
                    } else {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Image(
                                painter = painterResource(Res.drawable.baseline_file_present_24),
                                contentDescription = "",
                                modifier = Modifier.size(40.toScaleDp())
                            )
                            Text(
                                text = signatureFileId ?: "",
                                color = BlueApp,
                                fontSize = 12.toScaleSp(),
                                fontWeight = FontWeight.Normal,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.padding(
                                    start = 40.toScaleDp(),
                                    end = 40.toScaleDp()
                                )
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            Text(
                stringResource(Res.string.your_comment),
                fontWeight = FontWeight.W700,
                fontSize = 16.toScaleSp(),
            )
            Spacer(modifier = Modifier.height(8.toScaleDp()))
            AppInputField(
                viewModel.additionalNotesText,
                stringResource(Res.string.comment),
                stringResource(Res.string.additional_notes),
                isMaxLinesDefined = false,
                paddingTop = 0.toScaleDp()
            )
            Spacer(modifier = Modifier.height(16.toScaleDp()))
            CheckBoxMultipleItemBoldTitle(
                question = stringResource(Res.string.safety_requirements),
                answerList = arrayListOf(
                    stringResource(Res.string.yes),
                    stringResource(Res.string.no)
                ),
                checkedList = arrayListOf(
                    viewModel.healthAndSafetyState.value,
                    !viewModel.healthAndSafetyState.value
                ),
                onSelectedAnswer = { answerIndex ->
                    when (answerIndex) {
                        0 -> {
                            viewModel.healthAndSafetyState.value = true
                        }

                        1 -> {
                            viewModel.healthAndSafetyState.value = false
                        }
                    }
                }
            )
            if (!viewModel.healthAndSafetyState.value) {
                AppInputField(
                    viewModel.healthAndSafetyTextState,
                    stringResource(Res.string.safety_requirements_title),
                    stringResource(Res.string.safety_requirements_desc),
                    isMaxLinesDefined = false,
                    paddingTop = 0.toScaleDp()
                )
            }
            Spacer(modifier = Modifier.height(30.toScaleDp()))
            FilledButton(
                onClick = { submit() },
                modifier = Modifier.fillMaxWidth(),
                text = stringResource(Res.string.submit)
            )
        }
    }
}